/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { DataState } from '@/shared/ui/components/basic/data-state/DataState';
import { CardListSkeleton } from '@/shared/ui';
import StatusBadge from '@/shared/ui/components/basic/StatusBadge';
import type { SearchStatus } from '../context/PartnershipsWizardContext';
import { searchStatusToTone } from '@/shared/utils/partnerships-status';
import { buildSearchTitle } from '@/shared/utils/partnerships-search-record';
import { formatLocalizedDateTime } from '@/shared/utils/date';
import { SearchSummaryStats } from './SearchSummaryStats';

export interface GeoGroupSearch {
  search_id: string;
  status: SearchStatus;
  properties_retrieved: number;
  sources_count: number;
  agencies_count: number;
  created_at: string;
  city: string;
  neighbourhood?: string;
}

export interface GeoGroup {
  geo_key: string;
  city: string;
  neighbourhood?: string;
  searches: GeoGroupSearch[];
}

interface SearchHistoryByGeoProps {
  groups: GeoGroup[];
  loading: boolean;
  error: string | null;
  onSelect?: (searchId: string) => void;
}

/**
 * Renders the partner-search history grouped by geographic location, with
 * loading/error/empty handling delegated to {@link DataState}.
 *
 * @param props - The geo-grouped searches, load/error state, and select
 *   callback.
 * @returns The geo-grouped history list.
 */
export function SearchHistoryByGeo({
  groups,
  loading,
  error,
  onSelect,
}: SearchHistoryByGeoProps) {
  const { t } = useTranslation('features/partnerships');

  return (
    <DataState
      isLoading={loading}
      isError={!!error}
      isEmpty={groups.length === 0}
      loadingFallback={<CardListSkeleton count={2} />}
      errorMessage={error ?? t('results.error')}
      emptyState={
        <Text
          variant="body"
          size="sm"
          className="text-center text-[var(--text-secondary)]"
        >
          {t('history.empty')}
        </Text>
      }
    >
      <div
        className="flex flex-col gap-[var(--space-4)]"
        data-testid="history-geo-groups"
      >
        {groups.map((group) => (
          <GeoSection key={group.geo_key} group={group} onSelect={onSelect} />
        ))}
      </div>
    </DataState>
  );
}

/**
 * Renders one geographic group (city / neighbourhood) with its list of past
 * searches as selectable rows.
 *
 * @param props - The geo group to render and the row-select callback.
 * @returns The geo section.
 */
function GeoSection({
  group,
  onSelect,
}: {
  group: GeoGroup;
  onSelect?: (id: string) => void;
}) {
  const header = group.neighbourhood
    ? `${group.city} · ${group.neighbourhood}`
    : group.city;

  return (
    <section data-testid={`geo-section-${group.geo_key}`}>
      <Text variant="body" size="sm" fw={600} className="mb-[var(--space-2)]">
        {header}
      </Text>
      <div className="flex flex-col gap-[var(--space-2)]">
        {group.searches.map((s) => (
          <SearchRecordRow key={s.search_id} search={s} onSelect={onSelect} />
        ))}
      </div>
    </section>
  );
}

/**
 * One search record row: auto-title, searched-at date+time, the real agencies
 * and data-sources counts, and status.
 *
 * The row is the "unique search record" surface in the list — each past
 * search reads as its own titled entry (not just a date), and selecting it
 * opens that record's one-page. The second line carries the searched-at
 * date+TIME plus the per-search figures the broker scans the history by: how
 * many agencies and how many distinct data sources the search found.
 *
 * @param props - The search summary and the row-select callback.
 * @returns The selectable record row.
 */
function SearchRecordRow({
  search,
  onSelect,
}: {
  search: GeoGroupSearch;
  onSelect?: (id: string) => void;
}) {
  const { t } = useTranslation('features/partnerships');
  const title = buildSearchTitle(
    { city: search.city, neighbourhood: search.neighbourhood },
    {},
    t('page_title'),
  );
  return (
    <UnstyledButton
      type="button"
      onClick={() => onSelect?.(search.search_id)}
      data-testid={`search-record-${search.search_id}`}
      className="flex flex-col gap-[var(--space-1)] rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-[var(--space-3)] text-left shadow-[var(--shadow-sm)] transition-all duration-200 hover:bg-[var(--surface-secondary)]"
    >
      <div className="flex items-center justify-between gap-[var(--space-2)]">
        <Text variant="body" size="sm" fw={600}>{title}</Text>
        <StatusBadge tone={searchStatusToTone(search.status)}>
          {t(`history.status_${search.status}`)}
        </StatusBadge>
      </div>
      <div className="flex items-center justify-between gap-[var(--space-2)]">
        <div className="flex items-center gap-[var(--space-2)]">
          <Text variant="body" size="xs" c="dimmed">
            {t('history.searched_at', {
              datetime: formatLocalizedDateTime(search.created_at),
            })}
          </Text>
          <Text variant="body" size="xs" tone="tertiary" aria-hidden>·</Text>
          <SearchSummaryStats propertiesRetrieved={search.properties_retrieved} compact />
        </div>
      </div>
      <div className="flex items-center gap-[var(--space-2)]">
        <Text variant="body" size="xs" c="dimmed" data-testid={`search-agencies-${search.search_id}`}>
          {t('history.agencies_count', { count: search.agencies_count })}
        </Text>
        <Text variant="body" size="xs" c="dimmed">·</Text>
        <Text variant="body" size="xs" c="dimmed" data-testid={`search-sources-${search.search_id}`}>
          {t('history.sources_count', { count: search.sources_count })}
        </Text>
      </div>
    </UnstyledButton>
  );
}

